import { saveUpdateContent, CONTENT_STORE_KEYS } from '../../../../../../context-controller-mod/context-serv/global-provider/local-storage-context/updateContentStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/contentSectionEditPageNavigation.css';
import Cookies from 'js-cookie';
import ApiGet    from '../../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPut    from '../../../../../../api-controller-mod/api-serv/put-api/putApi';
import ApiDelete from '../../../../../../api-controller-mod/api-serv/delete-api/deleteApi';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import TextField from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import SelectMediaOverlay, {
  MediaItem as SelectMediaItem,
} from '../../../../../../component-controller-mod/component-serv/texteditor-fs-cp/select-media-overlay/ts/selectMediaOverlay';
import { Button, Confirmation, Toggle } from '@core/uilibrary';
import { useGlobalPopupProvider } from '../../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { DocConfirmationIcon, SuccessDocConfirmationIcon} from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import EditContentBreadcrumb from '../../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/edit-content-breadcrumb/ts/editContentBreadcrumb';

import {
  HeaderData, FooterData, HeaderSlugResponse, FooterSlugResponse,
  ApiMediaListItem, UpdateEntry, ActiveSection, MediaSaveCallback,
} from './types';
import { STATUS_OPTIONS, SECTION_HUMAN, SECTION_KEY } from './constants';
import { formatBytes } from './helpers';
import HeaderEditor from './headerEditor';
import FooterEditor from './footerEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const ContentSectionEditPageNavigation: React.FC = () => {
  const {
    sectionId,
    contentType,
    contentUuid: paramContentUuid,
    contentVersionUuid: paramContentVersionUuid,
  } = useParams<{ contentType: string; sectionId: string; contentUuid: string; contentVersionUuid: string }>();
  const navigate      = useNavigate();
  const { showToast } = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const activeSectionKey: ActiveSection = sectionId === 'footer' ? 'footer' : 'header';
  const sectionLabel = SECTION_HUMAN[activeSectionKey];

  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [metaStatus, setMetaStatus] = useState('PUBLISHED');

  const [headerContentId, setHeaderContentId] = useState('');
  const [headerData,      setHeaderData]      = useState<HeaderData | null>(null);
  const [headerVisible,   setHeaderVisible]   = useState(true);
  const [footerContentId, setFooterContentId] = useState('');
  const [footerData,      setFooterData]      = useState<FooterData | null>(null);

  const userId             = Cookies.get('userUuid')  ?? '';
  const username           = Cookies.get('username')  ?? '';
  const contentVersionUuid = paramContentVersionUuid ?? Cookies.get('contentVersionUuid') ?? '';

  const isSavedRef = useRef(false);
  const origStatus = useRef<string>('PUBLISHED');
  const origTitle  = useRef<string>('');
  const origAuthor = useRef<string>('');
  const origHeader = useRef<HeaderData | null>(null);
  const origFooter = useRef<FooterData | null>(null);

  const [mediaOverlayOpen,         setMediaOverlayOpen]         = useState(false);
  const [mediaOverlayItems,        setMediaOverlayItems]        = useState<SelectMediaItem[]>([]);
  const [mediaOverlayOnSave,       setMediaOverlayOnSave]       = useState<(() => MediaSaveCallback) | null>(null);
  const [mediaOverlayHasMoreState, setMediaOverlayHasMoreState] = useState(true);
  const [mediaOverlayLoadingMore,  setMediaOverlayLoadingMore]  = useState(false);

  const mediaUuidMap        = useRef<Map<number, string>>(new Map());
  const mediaOverlayPageRef = useRef(1);
  const mediaOverlayHasMore = useRef(true);
  const mediaOverlayLoading = useRef(false);
  const OVERLAY_PAGE_SIZE   = 12;

  // ── Fetch by slug ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      ApiGet<HeaderSlugResponse>(`${API_ENDPOINTS.GET_CMS_CONTENT}?slug=header`),
      ApiGet<FooterSlugResponse>(`${API_ENDPOINTS.GET_CMS_CONTENT}?slug=footer`),
    ])
      .then(([hRes, fRes]) => {
        if (cancelled) return;

        const hData = hRes?.data?.data;
        if (hData) {
          setHeaderContentId(prev => prev || String(hData.contentId ?? ''));
          setHeaderData(hData.data.header);
          setHeaderVisible(hData.data.header.visible ?? true);
          origHeader.current = hData.data.header;
        }

        const fData = fRes?.data?.data;
        if (fData) {
          setFooterContentId(prev => prev || String(fData.contentId ?? ''));
          setFooterData(fData.data.footer);
          origFooter.current = fData.data.footer;
        }

        origAuthor.current = username;

        if (activeSectionKey === 'header' && hData) {
          setMetaStatus(hData.status ?? 'PUBLISHED');
          origStatus.current = hData.status ?? 'PUBLISHED';
          origTitle.current  = hData.data.header.title ?? '';
        } else if (fData) {
          setMetaStatus(fData.status ?? 'PUBLISHED');
          origStatus.current = fData.status ?? 'PUBLISHED';
          origTitle.current  = fData.data.footer.title ?? '';
        }
      })
      .catch(err => { if (!cancelled) console.error('[NavigationEdit] fetch:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [activeSectionKey]);

  // ── Delete draft ───────────────────────────────────────────────────────────
  /**
   * isDelete logic:
   *  - key missing        → call delete API + remove cookie
   *  - isDelete === false → skip delete API, remove only isDelete key
   *  - isDelete === true  → call delete API + remove cookie
   */
  const deleteDraft = useCallback(async () => {
    if (!contentVersionUuid) return;

    const raw      = localStorage.getItem('isDelete');
    const isDelete = raw !== null
      ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
      : null;

    if (isDelete === false) {
      localStorage.removeItem('isDelete');
      return;
    }

    localStorage.removeItem('isDelete');
    try {
      await ApiDelete(
        `${API_ENDPOINTS.DELETE_DRAFT_CONTENT}?version=${contentVersionUuid}`
      );
    } catch (err) {
      console.warn('[NavigationEdit] delete draft failed:', err);
    } finally {
      Cookies.remove('contentVersionUuid');
    }
  }, [contentVersionUuid]);

  // ── On unmount ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!isSavedRef.current) {
        deleteDraft();
      } else {
        localStorage.removeItem('isDelete');
      }
    };
  }, [deleteDraft]);

  // ── Register media usage ───────────────────────────────────────────────────
  const registerMediaUsage = useCallback((mediaUuid: string) => {
    const contentUuid = activeSectionKey === 'header' ? headerContentId : footerContentId;
    if (!mediaUuid || !contentUuid) return;
    ApiGet(
      `/api/v1/media/usage?mediaUuid=${mediaUuid}&entityType=CONTENT&entityUuid=${contentUuid}`
    ).catch(err => console.warn('[MediaUsage] register failed:', err));
  }, [activeSectionKey, headerContentId, footerContentId]);

const buildUpdates = useCallback((nextStatus: string): UpdateEntry[] => {
  const updates:      UpdateEntry[] = [];
  const fieldUpdates: UpdateEntry[] = [];

  // ── Header ────────────────────────────────────────────────────────────────
  // ── Header ────────────────────────────────────────────────────────────────
if (activeSectionKey === 'header' && headerData && origHeader.current) {
  const orig = origHeader.current;
  const BASE = ['header'] as const;

  // section visible — always send
  fieldUpdates.push({ path: [...BASE, 'visible'],                  value: headerVisible });
  // navigation.visible — always send
  fieldUpdates.push({ path: [...BASE, 'navigation', 'visible'],    value: headerData.navigation.visible });
  // navigation.items — always send (carries each item's visible)
  fieldUpdates.push({ path: [...BASE, 'navigation', 'items'],      value: headerData.navigation.items });
  // cta.visible — always send
  fieldUpdates.push({ path: [...BASE, 'cta', 'visible'],           value: headerData.cta.visible });
  // logo.image.visible — always send
  fieldUpdates.push({ path: [...BASE, 'logo', 'image', 'visible'], value: headerData.logo.image.visible });

  if (headerData.cta.content.label !== orig.cta.content.label)
    fieldUpdates.push({ path: [...BASE, 'cta', 'content', 'label'], value: headerData.cta.content.label });

  if (headerData.logo.image.src !== orig.logo.image.src)
    fieldUpdates.push({ path: [...BASE, 'logo', 'image', 'src'],    value: headerData.logo.image.src });

  updates.push(...fieldUpdates);
  updates.push({ path: [...BASE, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
  fieldUpdates.length = 0;
}

  // ── Footer ────────────────────────────────────────────────────────────────
  if (activeSectionKey === 'footer' && footerData && origFooter.current) {
    const orig    = origFooter.current;
    const content = footerData.cta[0]?.layout?.content[0];
    const origC   = orig.cta[0]?.layout?.content[0];
    const BASE    = ['footer', 'cta', 0, 'layout', 'content', 0] as const;

    // footer section visible — always send
    fieldUpdates.push({ path: ['footer', 'visible'], value: footerData.visible });

    if (content && origC) {
      // footerLeft.visible — always send
      fieldUpdates.push({ path: [...BASE, 'footerLeft',     'visible'], value: content.footerLeft.visible });
      // footerRight.visible — always send
      fieldUpdates.push({ path: [...BASE, 'footerRight',    'visible'], value: content.footerRight.visible });
      // headerSection.visible — always send
      fieldUpdates.push({ path: [...BASE, 'headerSection',  'visible'], value: content.headerSection.visible });

      if (content.footerLeft.title !== origC.footerLeft.title)
        fieldUpdates.push({ path: [...BASE, 'footerLeft',  'title'],    value: content.footerLeft.title });

      const listsChanged = content.footerRight.lists.some((item, idx) => {
        const o = origC.footerRight.lists[idx];
        return !o || item.title !== o.title;
      });
      if (listsChanged)
        fieldUpdates.push({ path: [...BASE, 'footerRight', 'lists'],    value: content.footerRight.lists });

      if (content.headerSection.title !== origC.headerSection.title)
        fieldUpdates.push({ path: [...BASE, 'headerSection', 'title'],   value: content.headerSection.title });
      if (content.headerSection.imgLogo !== origC.headerSection.imgLogo)
        fieldUpdates.push({ path: [...BASE, 'headerSection', 'imgLogo'], value: content.headerSection.imgLogo });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: ['footer', 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
  }

  return updates;
}, [activeSectionKey, headerData, footerData, headerVisible]);
  // ── Save ───────────────────────────────────────────────────────────────────
  const performSave = useCallback(async () => {
    setSaving(true);
    const contentUuid = activeSectionKey === 'header' ? headerContentId : footerContentId;
    try {
      await ApiPut(
        `${API_ENDPOINTS.UPDATE_CONTENT_BY_PATH}?contentUuid=${contentUuid}&userId=${userId}&versionUuid=${contentVersionUuid}`,
        {
          type:    'page',
          title:   activeSectionKey === 'header' ? 'Header Layout' : 'Footer layout',
          version: 1,
          updates: buildUpdates(metaStatus),
        },
      );
      isSavedRef.current = true;
      localStorage.removeItem('isDelete');
      if (activeSectionKey === 'header') {
        saveUpdateContent(CONTENT_STORE_KEYS.HEADER, contentType ?? 'navigation', headerContentId, contentVersionUuid, 'header');
      } else {
        saveUpdateContent(CONTENT_STORE_KEYS.FOOTER, contentType ?? 'navigation', footerContentId, contentVersionUuid, 'footer');
      }
      Cookies.remove('contentVersionUuid');
      showToast({ status: 'success', title: 'Complete !',   description: 'Section was updated successfully.' });
      navigate(`/workflow/manage/preview/${contentUuid}/${contentVersionUuid}/draft`);
    } catch (err) {
      console.error('[NavigationEdit] save:', err);
      showToast({ status: 'error', title: 'Uncomplete !', description: 'Section was updated unsuccessfully.' });
    } finally {
      setSaving(false);
    }
  }, [activeSectionKey, headerContentId, footerContentId, contentVersionUuid, buildUpdates, metaStatus, userId, contentType, navigate, showToast]);

  // ── Scroll lock when media overlay is open ─────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = mediaOverlayOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mediaOverlayOpen]);

  // ── Fetch one page of media for the overlay ────────────────────────────────
  const fetchMediaPage = useCallback(async (page: number, isLoadMore = false) => {
    if (mediaOverlayLoading.current) return;
    mediaOverlayLoading.current = true;
    if (isLoadMore) setMediaOverlayLoadingMore(true);
    try {
      const res     = await ApiGet<{ success: boolean; data: { items: ApiMediaListItem[]; pagination: { hasNext: boolean } } }>(
        `${API_ENDPOINTS.GET_ALL_MEDIA}?page=${page}&size=${OVERLAY_PAGE_SIZE}`
      );
      const raw     = res?.data?.data?.items             ?? [];
      const hasNext = res?.data?.data?.pagination?.hasNext ?? false;

      const mapped: SelectMediaItem[] = raw.map((m: ApiMediaListItem) => ({
        id:       m.id,
        src:      m.url,
        alt:      m.alt  ?? '',
        title:    m.name ?? '',
        size:     m.size ? formatBytes(m.size) : '',
        isActive: m.status === 'ACTIVE' || m.status === 'PUBLISHED',
      }));

      raw.forEach((m: ApiMediaListItem) => {
        mediaUuidMap.current.set(m.id, m.uuid ?? '');
      });

      mediaOverlayHasMore.current = hasNext;
      setMediaOverlayHasMoreState(hasNext);
      setMediaOverlayItems(prev => isLoadMore ? [...prev, ...mapped] : mapped);
    } catch {
      showToast({ status: 'error', title: 'Error', description: 'Failed to load media.' });
      if (!isLoadMore) setMediaOverlayItems([]);
    } finally {
      mediaOverlayLoading.current = false;
      if (isLoadMore) setMediaOverlayLoadingMore(false);
    }
  }, [showToast]);

  // ── Open media overlay ─────────────────────────────────────────────────────
  const openMediaOverlay = useCallback((onSave: MediaSaveCallback) => {
    mediaOverlayPageRef.current = 1;
    mediaOverlayHasMore.current = true;
    mediaOverlayLoading.current = false;
    setMediaOverlayItems([]);
    setMediaOverlayHasMoreState(true);
    setMediaOverlayLoadingMore(false);
    setMediaOverlayOnSave(() => () => (item: SelectMediaItem | null) => {
      onSave(item);
      if (item) {
        const uuid = mediaUuidMap.current.get(item.id);
        if (uuid) registerMediaUsage(uuid);
      }
    });
    setMediaOverlayOpen(true);
    fetchMediaPage(1, false);
  }, [fetchMediaPage, registerMediaUsage]);

  // ── Load next media page ───────────────────────────────────────────────────
  const handleMediaLoadMore = useCallback(() => {
    if (!mediaOverlayHasMore.current || mediaOverlayLoading.current) return;
    const nextPage = mediaOverlayPageRef.current + 1;
    mediaOverlayPageRef.current = nextPage;
    fetchMediaPage(nextPage, true);
  }, [fetchMediaPage]);

  // ── Confirm save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Save changes"
        description={`Are you sure you want to save changes to the "${sectionLabel}" section? This will update the live content.`}
        status="info" icon={<SuccessDocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"   size="md" value="Cancel" type="button" onClick={closeConfirm} />
          <Button variant="primary" size="md" value="Save"   type="button"
            onClick={() => { closeConfirm(); performSave(); }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, sectionLabel, performSave]);

  // ── Confirm cancel ─────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Discard changes"
        description="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        status="warning" icon={<DocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"  size="md" value="Cancel"    type="button" onClick={closeConfirm} />
          <Button variant="danger" size="md" value="Discard" type="button"
            onClick={async () => {
              closeConfirm();
              await deleteDraft();
              isSavedRef.current = true;
              localStorage.removeItem('isDelete');
              navigate(`/content/${contentType ?? 'navigation'}`);
            }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, navigate, contentType, deleteDraft]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="scl--cms-content-edit-navigation-root">
        <div className="scl--cms-content-edit-navigation-skeleton-wrap">
          <div className="scl--cms-content-edit-navigation-skeleton scl--cms-content-edit-navigation-skeleton--title" />
          <div className="scl--cms-content-edit-navigation-skeleton scl--cms-content-edit-navigation-skeleton--desc"  />
          <div className="scl--cms-content-edit-navigation-skeleton scl--cms-content-edit-navigation-skeleton--field" />
          <div className="scl--cms-content-edit-navigation-skeleton scl--cms-content-edit-navigation-skeleton--field" />
          <div className="scl--cms-content-edit-navigation-skeleton scl--cms-content-edit-navigation-skeleton--field" />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-content-edit-navigation-root">
      <EditContentBreadcrumb
        onHomeClick={() => { localStorage.removeItem('isDelete'); navigate('/overview'); }}
        onContentClick={() => { localStorage.removeItem('isDelete'); navigate('/content/navigation'); }}
      />

      <h1 className="scl--cms-content-edit-navigation-page-title">Edit section</h1>
      <p className="scl--cms-content-edit-navigation-page-desc">
        Responsible for managing and maintaining website content to ensure accuracy.
      </p>

      <div className="scl--cms-content-edit-navigation-meta-grid">
        <TextField label="Section ID" value={SECTION_KEY[activeSectionKey]} disabled />
        <TextField label="Author"     value={origAuthor.current} disabled />
        <TextField label="Section"    value={sectionLabel}       disabled />
        <TextField label="Title"      value={origTitle.current}  disabled />
      </div>

      <div className="scl--cms-content-edit-navigation-section-editor">
        <div className="scl--cms-content-edit-navigation-section-heading-row">
          <h2 className="scl--cms-content-edit-navigation-section-heading">{sectionLabel}</h2>
          {activeSectionKey === 'header' && (
            <Toggle
              checked={headerVisible}
              onChange={(v: boolean) => {
                setHeaderVisible(v);
                setHeaderData(prev => prev ? { ...prev, visible: v } : prev);
              }}
            />
          )}
          {activeSectionKey === 'footer' && footerData && (
            <Toggle
              checked={footerData.visible}
              onChange={(v: boolean) => setFooterData(prev => prev ? { ...prev, visible: v } : prev)}
            />
          )}
        </div>
        <div className="scl--cms-content-edit-navigation-divider" />

        {activeSectionKey === 'header' && headerData && (
          <HeaderEditor
            data={headerData}
            onChange={setHeaderData}
            onMediaClick={openMediaOverlay}
            userId={userId}
            registerMediaUsage={registerMediaUsage}
          />
        )}
        {activeSectionKey === 'footer' && footerData && (
          <FooterEditor
            data={footerData}
            onChange={setFooterData}
            onMediaClick={openMediaOverlay}
            userId={userId}
            registerMediaUsage={registerMediaUsage}
          />
        )}
      </div>

      <div className="scl--cms-content-edit-navigation-actions">
        <Button variant="ghost"   value="Cancel"                      onClick={handleCancel} />
        <Button variant="primary" value={saving ? 'Saving…' : 'Save'} onClick={handleSave}  disabled={saving} />
      </div>

      {mediaOverlayOpen && (
        <SelectMediaOverlay
          items={mediaOverlayItems}
          hasMore={mediaOverlayHasMoreState}
          loadingMore={mediaOverlayLoadingMore}
          onLoadMore={handleMediaLoadMore}
          onSave={item => {
            if (mediaOverlayOnSave) { const cb = mediaOverlayOnSave(); cb(item); }
            setMediaOverlayOpen(false);
            setMediaOverlayOnSave(null);
          }}
          onClose={() => { setMediaOverlayOpen(false); setMediaOverlayOnSave(null); }}
        />
      )}
    </div>
  );
};

export default ContentSectionEditPageNavigation;